import { Controller } from '@hotwired/stimulus';

/**
 * This controller is responsible for reacting to user interaction with the Google map on our
 * project search page.
 *
 * Specifically:
 *
 *  1. When the user changes the map bounds:
 *
 *    1.1 We need to update the list of projects on our page if there is such a list, i.e. if we are
 *      currently in the desktop (not the mobile) layout. This update is triggered by sending a
 *      request to the URL {listPath}. It is expected that this endpoint returns a Turbo Stream,
 *      which updates the project list. So the controller renders the response as Turbo Stream.
 *
 *    1.2 We need to update the query parameters so that a full page reload brings the user to the
 *      same section of the map.
 *
 *  2. When the user clicks a marker, we need to render a preview. This is triggered by sending a
 *    request to the URL {previewPath}. It is expected that the endpoint returns a Turbo Stream,
 *    which replaces the currently open project previews with the requested ones. The controller
 *    renders the response as Turbo Stream.
 *
 * Connects to data-controller="gmaps-interaction"
 */
export default class extends Controller {
  static values = {
    previewPath: String,
    listPath: String,
    additionalQueryString: String,
  };
  static targets = ['map'];

  connect() {
    // When the user changes the map bounds, we set the zoom query parameter, see below. When the
    // user then reloads the page, the controller is connected again. Now, it should honor the
    // zoom query parameter.
    this.mapTarget.innerMap.setOptions({
      zoom: this.getZoomQueryValue(),
    });

    google.maps.event.addListener(
      this.mapTarget.innerMap,
      'bounds_changed',
      this.onBoundsChange.bind(this),
    );
  }

  onMarkerClick(event) {
    const projectsToPreview = event.target
      .closest('gmp-advanced-marker')
      .getAttribute('project-ids')
      .trim()
      .split(' ');

    const query = new URLSearchParams({ 'preview[]': projectsToPreview[0] });
    projectsToPreview.slice(1).forEach((id) => query.append('preview[]', id));
    this.streamRender(this.previewPathValue + '?' + query.toString());
  }

  /**
   * onBoundsChange does two things:
   *
   * 1. It updates the query parameters so that the page can be reloaded without changing the map
   *    bounds.
   * 2. On large screens, where a project list is rendered next to the map, it re-renders the
   *    project list so that it matches the markers shown on the map.
   */
  onBoundsChange() {
    // After the map is first rendered a bounds_changed event is fired. In this case, we don't want
    // to trigger the page update.
    if (!this.onBoundsChangeCalled) {
      this.onBoundsChangeCalled = true;
      return;
    }

    // Since onBoundsChange is a callback for the bounds_changed event, it is called very often when
    // the user changes the map bounds manually with drag & drop. So we employ a debounce pattern.
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      // Set the zoom and map_bounds query parameter.
      //
      // Why we need the zoom parameter: Apparently, the map cannot determine the correct zoom level
      // when given only the map bounds. This can lead to the problem that a greater area is shown
      // on the map than it should be.
      //
      // Steps that can lead to this problem:
      //
      //   1. Load the map in desktop mode.
      //   2. Zoom in so that, say, 3 markers are visible. You see now also the 3 corresponding
      //      search cards. The query string says "map_bounds=a/b/c/d".
      //   3. Reload the page. Now you still see the 3 search cards, but you might also see a bigger
      //      part of the map than in 2 with more markers.
      //
      // The reason for 3 seems to be that the zoom level of the map is wrong after the reload.
      //
      // To avoid this, we remember the current zoom level in the 'zoom' query parameter whenever we
      // change the map_bounds query parameter. The server ignores this parameter but this
      // controller honors it when initializing the map. This way, full page reloads don't expand
      // the area shown on the map.
      this.setQueryValue('zoom', this.mapTarget.innerMap.getZoom());

      const bounds = this.mapTarget.innerMap.getBounds();
      const north = bounds.getNorthEast().lat();
      const east = bounds.getNorthEast().lng();
      const south = bounds.getSouthWest().lat();
      const west = bounds.getSouthWest().lng();
      const boundsValue = `${north}/${east}/${south}/${west}`;

      const query = new URLSearchParams(window.location.search);
      query.set('map_bounds', boundsValue);
      this.setQueryValue('map_bounds', boundsValue);

      const additionalQueryParams = new URLSearchParams(
        this.additionalQueryStringValue,
      );
      for (const [key, value] of additionalQueryParams.entries()) {
        query.set(key, value);
      }

      // Now, re-render the project list so it matches the markers visible on the map.
      //
      // This re-rendering is not necessary in the mobile layout because here the project list is
      // not visible. That's why we check the --layout custom property. Because getComputedStyle can
      // have a negative impact on performance, we do this inside the timeout callback.

      if (
        getComputedStyle(this.element).getPropertyValue('--layout') == 'mobile'
      ) {
        return;
      }

      this.streamRender(this.listPathValue + '?' + query.toString());
    }, 400);
  }

  streamRender(location) {
    fetch(location, {
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
      },
    })
      .then((r) => r.text())
      .then((html) => Turbo.renderStreamMessage(html));
  }

  getZoomQueryValue() {
    const value = new URLSearchParams(window.location.search).get('zoom');
    return parseInt(value) || undefined;
  }

  setQueryValue(name, value) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(name, value);
    history.replaceState(null, '', '?' + searchParams.toString());
  }
}
